/**
 * AddSourceMenu - ONE way to add something, in both sections.
 *
 * The old toolbar changed with the tab: a primary "Upload Case Files" on one,
 * an outline "Add Law" on another, nothing at all on Preparatory Works. The
 * button you wanted was in a different place, at a different weight, with a
 * different label depending on where you happened to be - so "add a document"
 * had no muscle memory.
 *
 * This is the same control everywhere: one primary "Add", three destinations,
 * each naming where the thing will land. The Generate popover stays separate —
 * asking the AI to go and find sources is not the same act as adding one.
 */

import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Menu, Transition } from '@headlessui/react';
import {
  PiCaretDownLight,
  PiCloudArrowUpLight,
  PiGavelLight,
  PiPlusLight,
  PiScalesLight,
} from 'react-icons/pi';
import Button from '../../../shared/TailwindComponents/Button';

interface Props {
  canEdit: boolean;
  /** Case law is not offered where the jurisdiction has no such corpus. */
  showCases: boolean;
  onUploadFiles: () => void;
  onAddLegislation: () => void;
  onAddPrecedent: () => void;
}

const AddSourceMenu: React.FC<Props> = ({
  canEdit,
  showCases,
  onUploadFiles,
  onAddLegislation,
  onAddPrecedent,
}) => {
  const { t } = useTranslation();

  const items = [
    {
      key: 'upload',
      label: t('Upload files'),
      hint: t('Goes to Case files'),
      icon: <PiCloudArrowUpLight className="h-4 w-4" />,
      onClick: onUploadFiles,
    },
    {
      key: 'legislation',
      label: t('Add legislation'),
      hint: t('Goes to Legal authority'),
      icon: <PiScalesLight className="h-4 w-4" />,
      onClick: onAddLegislation,
    },
    ...(showCases
      ? [{
          key: 'precedent',
          label: t('Add precedent'),
          hint: t('Goes to Legal authority'),
          icon: <PiGavelLight className="h-4 w-4" />,
          onClick: onAddPrecedent,
        }]
      : []),
    /*
      TODO(add-link): "Add link" - paste a URL to a source hosted elsewhere.
      Deliberately absent: `/matters/sources` has no link-only source type, so
      the row would have no blob to stream and the reader would open on
      nothing. Needs a backend type before it can be offered.
    */
  ];

  if (!canEdit) {
    return (
      <Button
        variant="primary"
        size="small"
        disabled
        tooltip={t('You need edit access to add documents. Contact an editor or admin.')}
        icon={<PiPlusLight className="h-3.5 w-3.5" />}
      >
        {t('Add')}
      </Button>
    );
  }

  return (
    <Menu as="div" className="relative inline-block text-start" data-testid="add-source-menu">
      <Menu.Button as={Button} variant="primary" size="small" icon={<PiPlusLight className="h-3.5 w-3.5" />}>
        <span className="flex items-center gap-1">
          {t('Add')}
          <PiCaretDownLight className="h-3 w-3" />
        </span>
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-pop duration-150"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <Menu.Items
          className="glass absolute end-0 z-50 mt-2 w-60 origin-top-right rounded-lg
                     border border-hairline-soft shadow-lg focus:outline-none p-1"
        >
          {items.map((item) => (
            <Menu.Item key={item.key}>
              {({ active }) => (
                <button
                  type="button"
                  data-testid={`add-source-${item.key}`}
                  onClick={item.onClick}
                  className={clsx(
                    'flex w-full items-start gap-2 rounded-md px-2 py-2 text-start',
                    active ? 'bg-surface-sunken' : ''
                  )}
                >
                  <span className="mt-0.5 text-fg-muted flex-shrink-0">{item.icon}</span>
                  <span className="min-w-0">
                    <span className="block text-sm text-fg-body">{item.label}</span>
                    {/* Says where it lands - the fix for uploads that seemed
                        to vanish because they were re-routed silently. */}
                    <span className="block text-[10px] text-fg-muted">{item.hint}</span>
                  </span>
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default AddSourceMenu;
